import React from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import Parse from 'parse'
import { getSourceMeta } from './swimCoachConversationSource'
import SwimCoachModelBadges from './SwimCoachModelBadges'
import { MODEL_FIELD, MODEL_NOTE, modelsUsed } from './swimCoachModel'
import {
  COACH_DIMENSIONS,
  EVALUATION_CLASS,
  EVALUATION_FIELD,
  MAX_DIMENSION_SCORE,
  SAMPLING_NOTE,
  SCORE_TITLE,
  formatScore,
  indexEvaluationsByMessage,
  scoreBadgeClass,
  summarizeEvaluations
} from './swimCoachRating'

const MESSAGE_LIMIT = 1000

// Mirrors SWIM_COACH_LOCK_CATEGORIES in the backend
// (cloud/swim-coach/types/swim-coach-types.ts). Kept in sync manually because
// the admin app writes the lock fields directly rather than via the backend.
const LOCK_CATEGORIES = [
  'self_harm',
  'disordered_eating',
  'medical_emergency',
  'medical_advice_overreach',
  'injury_overreach',
  'persistent_off_topic',
  'sycophancy_harmful',
  'reverse_engineering',
  'terms_violation',
  'other_harm'
]

// Mirrors SwimCoachMessageStatus in the backend
// (cloud/swim-coach/types/swim-coach-types.ts). 'complete' is deliberately
// absent: it's the uninteresting case, and legacy rows written before the field
// existed have no status at all, which the backend also surfaces as complete.
// Anything else means the reply on screen is not a finished answer, which is
// otherwise impossible to tell from a short one — the whole point of showing it.
const MESSAGE_STATUS_META = {
  failed: {
    badgeClass: 'text-bg-danger',
    borderClass: 'border-danger',
    title: 'The turn failed. Any content is a partial reply the coach never ' +
      'finished — not an answer. Check Sentry for the cause.'
  },
  flagged: {
    badgeClass: 'text-bg-danger',
    borderClass: 'border-danger',
    title: 'The safety reviewer replaced the reply with the canned lock text. ' +
      'The coach\'s original words are not stored.'
  },
  streaming: {
    badgeClass: 'text-bg-warning',
    borderClass: 'border-warning',
    title: 'Still streaming, or the turn died without ever finalizing this ' +
      'row. Content is whatever had been written through when it stopped.'
  },
  pending: {
    badgeClass: 'text-bg-secondary',
    borderClass: 'border-secondary',
    title: 'Queued and never started.'
  }
}

const DEFAULT_LOCK_CATEGORY = 'other_harm'
// Matches the backend's canned user-facing lock text (localizedSafetyLockMessage
// in swim-coach-safety-agent.ts) so a manual lock reads the same to the swimmer.
const DEFAULT_LOCK_MESSAGE =
  'This conversation has been paused for safety. ' +
  'For help, please contact support@myswimpro.com.'
const DEFAULT_LOCK_REASON = 'Manual lock by admin'

function defaultLockForm () {
  return {
    category: DEFAULT_LOCK_CATEGORY,
    message: DEFAULT_LOCK_MESSAGE,
    reason: DEFAULT_LOCK_REASON
  }
}

// Not every thrown value is an Error with a usable message (e.g. Parse can
// reject with a plain object), so fall back to String(error) rather than
// rendering a blank error banner.
function errorMessage (error) {
  if (error && error.message) {
    return error.message
  }
  return String(error)
}

function formatJson (value) {
  if (value === undefined || value === null) {
    return '—'
  }
  if (typeof value === 'string') {
    return value
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch (error) {
    return String(value)
  }
}

// A dimension the evaluator marked N/A. Its weight was redistributed across the
// dimensions that were scored, so this is not the same as a 0 and must not read
// like one.
function renderDimensionScore (score) {
  if (typeof score !== 'number') {
    return (
      <span
        className="badge text-bg-secondary"
        title="Not applicable to this response; its weight was redistributed across the other dimensions."
      >
        N/A
      </span>
    )
  }
  return (
    <span className="badge text-bg-light">
      {score + ' / ' + MAX_DIMENSION_SCORE}
    </span>
  )
}

function renderGates (evaluation) {
  const gates = evaluation.get(EVALUATION_FIELD.GATES)
  if (!gates || !gates.length) {
    return null
  }
  return (
    <div className="alert alert-danger py-2 px-2 mb-2">
      <b>Hard failure — coach score forced to 0</b>
      <ul className="mb-0 ps-3">
        {gates.map((gate, index) => (
          <li key={index}>
            <code>{gate.type}</code>
            {gate.evidence && (
              <span className="ms-1">— &ldquo;{gate.evidence}&rdquo;</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function renderSource (entityType, entityId) {
  const meta = getSourceMeta(entityType)
  if (!meta || !entityId) {
    return (<span className="text-muted">—</span>)
  }
  return (
    <a href={'/#' + meta.path + entityId}>
      {meta.label} <code className="small">{entityId}</code>
    </a>
  )
}

export default class SwimCoachConversationDetailComponent extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      conversation: null,
      messages: null,
      // Distinct models across the loaded replies, oldest first. Derived once
      // on load, like evaluationsByMessage, rather than on every render.
      models: [],
      safetyReviews: null,
      evaluations: null,
      // Ratings keyed by the id of the message they scored. Built once on load
      // rather than per message, so a long transcript doesn't re-index the
      // whole rating set on every row it draws.
      evaluationsByMessage: {},
      // Ratings that came back for turns outside the loaded transcript. Only
      // ever non-zero on a conversation long enough to hit the message cap.
      evaluationsDropped: 0,
      evaluationsError: null,
      loadError: null,
      safetyReviewsError: null,
      copied: false,
      isLockFormOpen: false,
      lockForm: defaultLockForm(),
      lockActionInProgress: false,
      lockActionError: null
    }
    this.copyResetTimeout = null
    if (this.props.match && this.props.match.params.id) {
      this.loadConversation(this.props.match.params.id)
    }
  }

  loadConversation = async (conversationId) => {
    let conversation
    let messages
    try {
      const conversationQuery = new Parse.Query('SwimCoachConversation')
      conversation = await conversationQuery.get(conversationId)

      const messageQuery = new Parse.Query('SwimCoachMessage')
      messageQuery.equalTo('conversation', conversation)
      messageQuery.ascending('createdAt')
      messageQuery.limit(MESSAGE_LIMIT)
      messages = await messageQuery.find()
    } catch (error) {
      console.error('Error loading conversation:', error)
      this.setState({ loadError: error.message })
      return
    }

    this.setState({ conversation, messages, models: modelsUsed(messages) })

    try {
      const reviewQuery = new Parse.Query('SwimCoachSafetyReview')
      reviewQuery.equalTo('conversation', conversation)
      reviewQuery.ascending('createdAt')
      reviewQuery.limit(MESSAGE_LIMIT)
      const safetyReviews = await reviewQuery.find()
      this.setState({ safetyReviews })
    } catch (error) {
      console.error('Error loading safety reviews:', error)
      this.setState({ safetyReviewsError: errorMessage(error) })
    }

    // Evaluator ratings, one row per scored coach response. Loaded after the
    // transcript and in its own try/catch for the same reason as the safety
    // reviews: a conversation still has to be readable when a secondary class
    // is unavailable.
    try {
      const evaluationQuery = new Parse.Query(EVALUATION_CLASS)
      evaluationQuery.equalTo('conversation', conversation)
      evaluationQuery.ascending(EVALUATION_FIELD.TURN_INDEX)
      evaluationQuery.limit(MESSAGE_LIMIT)
      const loaded = await evaluationQuery.find()
      // Both queries are capped independently, so on a transcript long enough
      // to hit the cap the two sets can cover different turns. Keeping only the
      // ratings whose message is actually on screen is what makes the summary
      // card's "cannot disagree with the ratings below it" property true rather
      // than merely likely.
      // The local, not this.state.messages: setState is not guaranteed to have
      // flushed by the time this runs, and a stale read here would drop every
      // rating.
      const renderedMessageIds = new Set(messages.map((message) => message.id))
      const evaluations = loaded.filter((evaluation) => {
        const message = evaluation.get(EVALUATION_FIELD.MESSAGE)
        return message && renderedMessageIds.has(message.id)
      })
      this.setState({
        evaluations,
        evaluationsByMessage: indexEvaluationsByMessage(evaluations),
        evaluationsDropped: loaded.length - evaluations.length
      })
    } catch (error) {
      console.error('Error loading evaluations:', error)
      // errorMessage, not error.message: Parse can reject with a plain object,
      // which would render the banner as "Evaluator ratings unavailable: ."
      this.setState({ evaluationsError: errorMessage(error) })
    }
  }

  componentWillUnmount () {
    if (this.copyResetTimeout) {
      clearTimeout(this.copyResetTimeout)
    }
  }

  buildPlaintext = () => {
    const { conversation, messages, models, safetyReviews } = this.state
    if (!conversation) return ''
    const lines = []
    const userPointer = conversation.get('user')
    const userId = userPointer ? userPointer.id : '(unknown)'
    const lastMessageAt = conversation.get('lastMessageAt')
    const entityType = conversation.get('entityType')
    const entityId = conversation.get('entityId')

    lines.push('Swim Coach Conversation ' + conversation.id)
    lines.push('User ID: ' + userId)
    lines.push('Messages: ' + (messages ? messages.length : 0))
    lines.push('Model: ' + (models.length ? models.join(', ') : 'not recorded'))
    if (lastMessageAt) {
      lines.push('Last Message: ' + lastMessageAt.toLocaleString())
    }
    const sourceMeta = getSourceMeta(entityType)
    if (sourceMeta && entityId) {
      lines.push('Source: ' + sourceMeta.label + ' (' + entityId + ')')
    }

    const lockedAt = conversation.get('lockedAt')
    if (lockedAt) {
      lines.push('Locked at: ' + lockedAt.toLocaleString())
      const lockCategory = conversation.get('lockCategory')
      const lockReason = conversation.get('lockReason')
      const lockMessage = conversation.get('lockMessage')
      if (lockCategory) lines.push('  Lock category: ' + lockCategory)
      if (lockReason) lines.push('  Internal reason: ' + lockReason)
      if (lockMessage) lines.push('  User-facing message: ' + lockMessage)
    }

    const summary = conversation.get('summary')
    if (summary) {
      const summarizedMessageCount = conversation.get('summarizedMessageCount')
      const summarizedThroughDate = conversation.get('summarizedThroughDate')
      lines.push('')
      lines.push('Conversation Summary (compressed history):')
      if (typeof summarizedMessageCount === 'number') {
        lines.push('  Summarized messages: ' + summarizedMessageCount)
      }
      if (summarizedThroughDate) {
        lines.push('  Summarized through: ' + summarizedThroughDate.toLocaleString())
      }
      lines.push(summary)
    }

    if (safetyReviews && safetyReviews.length) {
      lines.push('')
      lines.push('Safety Reviews (' + safetyReviews.length + '):')
      safetyReviews.forEach((review) => {
        const createdAt = review.get('createdAt')
        const when = createdAt ? createdAt.toLocaleString() : ''
        lines.push('  [' + review.get('status') + '] ' +
          (review.get('category') || '') + ' — ' +
          (review.get('reason') || '') + ' (' + when + ')')
      })
    }

    lines.push('')
    lines.push('----------------------------------------')
    lines.push('')

    if (messages && messages.length) {
      messages.forEach((message) => {
        const author = message.get('author') === 'user' ? 'User' : 'Assistant'
        const createdAt = message.get('createdAt')
        const when = createdAt ? createdAt.toLocaleString() : ''
        // Carry the status into the export too. A pasted transcript is usually
        // the first thing anyone looks at, and without this a reply that was cut
        // off reads exactly like a complete one.
        const status = message.get('status')
        const statusNote = MESSAGE_STATUS_META[status] ? ' [' + status + ']' : ''
        const model = message.get(MODEL_FIELD)
        const modelNote = model ? ' (' + model + ')' : ''
        lines.push('[' + when + '] ' + author + modelNote + statusNote + ':')
        lines.push(message.get('content') || '')
        const toolCalls = message.get('toolCalls')
        if (toolCalls && toolCalls.length) {
          toolCalls.forEach((toolCall) => {
            lines.push('  • tool: ' + toolCall.toolName)
            lines.push('    args: ' + formatJson(toolCall.args))
            lines.push('    result: ' + formatJson(toolCall.result))
          })
        }
        lines.push('')
      })
    }

    return lines.join('\n')
  }

  copyConversation = async () => {
    try {
      await navigator.clipboard.writeText(this.buildPlaintext())
      this.setState({ copied: true })
      if (this.copyResetTimeout) {
        clearTimeout(this.copyResetTimeout)
      }
      this.copyResetTimeout = setTimeout(() => {
        this.setState({ copied: false })
      }, 2000)
    } catch (error) {
      console.error('Failed to copy conversation to clipboard:', error)
    }
  }

  handleOpenLockForm = () => {
    this.setState({
      isLockFormOpen: true,
      lockActionError: null,
      lockForm: defaultLockForm()
    })
  }

  handleCancelLockForm = () => {
    this.setState({ isLockFormOpen: false, lockActionError: null })
  }

  handleLockFormChange = (event) => {
    const { name, value } = event.target
    this.setState((prevState) => ({
      lockForm: { ...prevState.lockForm, [name]: value }
    }))
  }

  handleConfirmLock = async () => {
    const { conversation, lockForm } = this.state
    if (!conversation) {
      return
    }
    const message = lockForm.message.trim()
    if (!message) {
      this.setState({
        lockActionError: 'A user-facing message is required to lock.'
      })
      return
    }
    this.setState({ lockActionInProgress: true, lockActionError: null })
    try {
      conversation.set('lockedAt', new Date())
      conversation.set('lockCategory', lockForm.category)
      conversation.set('lockMessage', message)
      conversation.set('lockReason', lockForm.reason.trim())
      await conversation.save()
      this.setState({
        conversation,
        isLockFormOpen: false,
        lockActionInProgress: false
      })
    } catch (error) {
      console.error('Error locking conversation:', error)
      // Roll back the unsaved field changes so a failed save doesn't leave the
      // UI showing the conversation as locked.
      conversation.revert()
      this.setState({
        lockActionInProgress: false,
        lockActionError: errorMessage(error)
      })
    }
  }

  handleUnlock = async () => {
    const { conversation } = this.state
    if (!conversation) {
      return
    }
    const confirmed = window.confirm(
      'Unlock this conversation? The swimmer will be able to send messages ' +
      'again and the lock details will be cleared.'
    )
    if (!confirmed) {
      return
    }
    this.setState({ lockActionInProgress: true, lockActionError: null })
    try {
      conversation.unset('lockedAt')
      conversation.unset('lockCategory')
      conversation.unset('lockMessage')
      conversation.unset('lockReason')
      await conversation.save()
      this.setState({ conversation, lockActionInProgress: false })
    } catch (error) {
      console.error('Error unlocking conversation:', error)
      // Roll back the unsaved unsets so a failed save doesn't leave the UI
      // showing the conversation as unlocked.
      conversation.revert()
      this.setState({
        lockActionInProgress: false,
        lockActionError: errorMessage(error)
      })
    }
  }

  renderLockActionError = () => {
    const { lockActionError } = this.state
    if (!lockActionError) {
      return null
    }
    return (
      <div className="alert alert-danger" role="alert">
        <b>Lock action failed:</b> {lockActionError}
      </div>
    )
  }

  renderLockControls = () => {
    const { conversation, isLockFormOpen, lockForm, lockActionInProgress } =
      this.state
    if (!conversation) {
      return null
    }
    // Unlock lives inside the lock banner, so only surface Lock controls here.
    if (conversation.get('lockedAt')) {
      return null
    }
    if (!isLockFormOpen) {
      return (
        <div className="mb-3">
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={this.handleOpenLockForm}
          >
            🔒 Lock conversation
          </button>
        </div>
      )
    }
    return (
      <div className="card border-danger mb-3">
        <div className="card-header text-bg-danger">
          Lock conversation for safety
        </div>
        <div className="card-body">
          <div className="mb-3">
            <label htmlFor="lockCategory" className="form-label">Category</label>
            <select
              id="lockCategory"
              name="category"
              className="form-select form-select-sm"
              value={lockForm.category}
              onChange={this.handleLockFormChange}
              disabled={lockActionInProgress}
            >
              {LOCK_CATEGORIES.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="lockMessage" className="form-label">
              User-facing message
              <span className="text-muted small ms-1">
                (shown to the swimmer)
              </span>
            </label>
            <textarea
              id="lockMessage"
              name="message"
              className="form-control form-control-sm"
              rows="3"
              value={lockForm.message}
              onChange={this.handleLockFormChange}
              disabled={lockActionInProgress}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="lockReason" className="form-label">
              Internal reason
              <span className="text-muted small ms-1">
                (admin only, not shown to the swimmer)
              </span>
            </label>
            <textarea
              id="lockReason"
              name="reason"
              className="form-control form-control-sm"
              rows="2"
              value={lockForm.reason}
              onChange={this.handleLockFormChange}
              disabled={lockActionInProgress}
            />
          </div>
          <button
            className="btn btn-sm btn-danger me-2"
            onClick={this.handleConfirmLock}
            disabled={lockActionInProgress}
          >
            {lockActionInProgress ? 'Locking...' : 'Confirm lock'}
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={this.handleCancelLockForm}
            disabled={lockActionInProgress}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  renderCopyButton = () => {
    const { conversation, copied } = this.state
    if (!conversation) return null
    return (
      <button
        className="btn btn-sm btn-outline-secondary mb-3"
        onClick={this.copyConversation}
      >
        {copied ? 'Copied!' : 'Copy as plaintext'}
      </button>
    )
  }

  renderLockBanner = () => {
    const { conversation, lockActionInProgress } = this.state
    if (!conversation) return null
    const lockedAt = conversation.get('lockedAt')
    if (!lockedAt) return null
    const category = conversation.get('lockCategory')
    const message = conversation.get('lockMessage')
    const reason = conversation.get('lockReason')
    return (
      <div className="alert alert-danger" role="alert">
        <h5 className="alert-heading">
          🔒 Conversation Locked
          {category && (<span className="ms-2 badge text-bg-light">{category}</span>)}
        </h5>
        <p className="mb-1"><b>Locked at:</b> {lockedAt.toLocaleString()}</p>
        {reason && (<p className="mb-1"><b>Internal reason:</b> {reason}</p>)}
        {message && (
          <div>
            <b>User-facing message:</b>
            <div className="mt-1 p-2 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
              {message}
            </div>
          </div>
        )}
        <div className="mt-3">
          <button
            className="btn btn-sm btn-light"
            onClick={this.handleUnlock}
            disabled={lockActionInProgress}
          >
            {lockActionInProgress ? 'Unlocking...' : '🔓 Unlock conversation'}
          </button>
        </div>
      </div>
    )
  }

  // Summary of the evaluator ratings on this conversation.
  //
  // Computed from the loaded rows rather than read off the conversation's
  // denormalized rollup, so it can never disagree with the per-turn ratings
  // shown directly beneath it. The rollup exists for the list view, where
  // sorting and filtering have to happen server-side.
  renderRatingSummary = () => {
    const { conversation, evaluations, evaluationsError, evaluationsDropped } =
      this.state
    if (!conversation || evaluationsError) {
      return null
    }
    if (!evaluations) {
      return null
    }
    const summary = summarizeEvaluations(evaluations)
    if (!summary) {
      return (
        <div className="card mb-3">
          <div className="card-body py-2">
            <span className="text-muted">
              <b>Not rated.</b> {SAMPLING_NOTE}
            </span>
          </div>
        </div>
      )
    }
    const { avgCoachScore, avgCxScore, evaluatedTurns, gatedTurns } = summary
    return (
      <div className="card mb-3">
        <div className="card-header">
          <b>Evaluator Rating</b>
          <span className="text-muted small ms-2">
            (averaged over {evaluatedTurns}
            {evaluatedTurns === 1 ? ' rated response' : ' rated responses'})
          </span>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-sm-3">
              <div className="text-muted small" title={SCORE_TITLE.COACH}>
                Coach Score
              </div>
              <span className={'badge ' + scoreBadgeClass(avgCoachScore, false)}>
                {formatScore(avgCoachScore)}
              </span>
            </div>
            <div className="col-sm-3">
              <div className="text-muted small" title={SCORE_TITLE.CX}>
                CX Score
              </div>
              <span className={'badge ' + scoreBadgeClass(avgCxScore, false)}>
                {formatScore(avgCxScore)}
              </span>
            </div>
            <div className="col-sm-3">
              <div className="text-muted small">Rated Responses</div>
              {evaluatedTurns}
            </div>
            <div className="col-sm-3">
              <div className="text-muted small">Gated</div>
              {gatedTurns > 0
                ? (<span className="badge text-bg-danger">{gatedTurns}</span>)
                : (<span className="text-muted">0</span>)}
            </div>
          </div>
          {evaluationsDropped > 0 && (
            <div className="alert alert-warning py-2 px-2 mt-3 mb-0">
              {evaluationsDropped} further
              {evaluationsDropped === 1 ? ' rating covers a turn' : ' ratings cover turns'}
              {' '}outside the loaded transcript and
              {evaluationsDropped === 1 ? ' is' : ' are'} excluded here, so this
              conversation is rated over more responses than the page shows.
            </div>
          )}
          <div className="small text-muted mt-3">
            The two scores are deliberately independent: a high CX score with a
            low coach score is sycophancy, and a low CX score with a high coach
            score is often the coach correctly telling a swimmer something they
            did not want to hear. Expand a rating below for the reasoning.
          </div>
        </div>
      </div>
    )
  }

  // One response's rating, inline in the transcript. Collapsed by default: the
  // badges answer "was this response any good", and the body answers "why",
  // which is only worth the space once someone is looking into a specific turn.
  renderMessageRating = (message) => {
    if (!message.id) {
      return null
    }
    const evaluation = this.state.evaluationsByMessage[message.id]
    if (!evaluation) {
      return null
    }
    const gated = Boolean(evaluation.get(EVALUATION_FIELD.GATED))
    const coachScore = evaluation.get(EVALUATION_FIELD.COACH_SCORE)
    const cxScore = evaluation.get(EVALUATION_FIELD.CX_SCORE)
    const reaction = evaluation.get(EVALUATION_FIELD.USER_REACTION)
    const intent = evaluation.get(EVALUATION_FIELD.PRIMARY_INTENT)
    return (
      <details className="small bg-light border rounded mt-2">
        <summary className="px-2 py-1">
          <span className={'badge me-1 ' + scoreBadgeClass(coachScore, gated)}>
            {'Coach ' + formatScore(coachScore)}
          </span>
          <span className={'badge me-2 ' + scoreBadgeClass(cxScore, false)}>
            {'CX ' + formatScore(cxScore)}
          </span>
          {gated && (
            <span className="badge text-bg-danger me-2">gated</span>
          )}
          {intent && (<code className="me-2">{intent}</code>)}
          {reaction && (
            <span className="text-muted">
              {'swimmer ' + reaction}
            </span>
          )}
        </summary>
        <div className="px-2 pb-2">
          {renderGates(evaluation)}
          {this.renderCoachDimensions(evaluation)}
          {this.renderCxReasoning(evaluation)}
          <div className="text-muted mt-2">
            {'Evaluator: ' +
              (evaluation.get(EVALUATION_FIELD.EVALUATOR_VERSION) || 'unknown')}
          </div>
        </div>
      </details>
    )
  }

  renderCoachDimensions = (evaluation) => {
    return (
      <div>
        <div className="text-muted small">Coach dimensions</div>
        {COACH_DIMENSIONS.map((dimension) => (
          <div key={dimension.key} className="mb-1">
            <b>{dimension.label}</b>
            <span className="text-muted ms-1">
              {'(weight ' + dimension.weight + ')'}
            </span>
            <span className="ms-2">
              {renderDimensionScore(evaluation.get(dimension.key))}
            </span>
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {evaluation.get(dimension.key + 'Rationale')}
            </div>
          </div>
        ))}
      </div>
    )
  }

  renderCxReasoning = (evaluation) => {
    const evidence = evaluation.get(EVALUATION_FIELD.CX_EVIDENCE)
    return (
      <div className="mt-2">
        <div className="text-muted small">Swimmer experience</div>
        <span className="ms-0">
          {renderDimensionScore(evaluation.get(EVALUATION_FIELD.CX))}
        </span>
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {evaluation.get(EVALUATION_FIELD.CX_RATIONALE)}
        </div>
        {evidence
          ? (
            <div className="fst-italic mt-1">
              &ldquo;{evidence}&rdquo;
            </div>
            )
          : (
            <div className="text-muted mt-1">
              No quote cited from the swimmer&apos;s reply.
            </div>
            )}
      </div>
    )
  }

  renderSafetyReviews = () => {
    const { safetyReviews } = this.state
    if (!safetyReviews || !safetyReviews.length) return null
    return (
      <div className="card mb-3">
        <div className="card-header">
          <b>Safety Reviews</b>
          <span className="text-muted ms-2">({safetyReviews.length})</span>
        </div>
        <ul className="list-group list-group-flush">
          {safetyReviews.map((review, index) => {
            const status = review.get('status')
            const category = review.get('category')
            const reason = review.get('reason')
            const createdAt = review.get('createdAt')
            const badgeClass = status === 'lock' ? 'text-bg-danger' : 'text-bg-warning'
            return (
              <li key={index} className="list-group-item">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className={'badge ' + badgeClass + ' me-2'}>{status}</span>
                    <code>{category}</code>
                    <span className="ms-2">{reason}</span>
                  </div>
                  <span className="text-muted small">
                    {createdAt ? createdAt.toLocaleString() : ''}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  renderHeader = () => {
    const {
      conversation, messages, models, safetyReviews, safetyReviewsError
    } = this.state
    if (!conversation) return null
    const userPointer = conversation.get('user')
    const userId = userPointer ? userPointer.id : null
    const lastMessageAt = conversation.get('lastMessageAt')
    const entityType = conversation.get('entityType') || null
    const entityId = conversation.get('entityId') || null
    let warnDisplay
    if (safetyReviewsError) {
      warnDisplay = (<span title="Safety reviews could not be loaded">?</span>)
    } else if (!safetyReviews) {
      warnDisplay = '...'
    } else {
      warnDisplay = safetyReviews.filter((r) => r.get('status') === 'warn').length
    }
    return (
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-sm-3">
              <div className="text-muted small">User ID</div>
              {userId
                ? (<a href={'/#/members/' + userId}><code>{userId}</code></a>)
                : (<code>(unknown)</code>)}
            </div>
            <div className="col-sm-3">
              <div className="text-muted small">Messages</div>
              {messages ? messages.length : '...'}
            </div>
            <div className="col-sm-3">
              <div className="text-muted small">Warnings</div>
              {warnDisplay}
            </div>
            <div className="col-sm-3">
              <div className="text-muted small">Last Message</div>
              {lastMessageAt ? lastMessageAt.toLocaleString() : ''}
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-sm-6">
              <div className="text-muted small">Source</div>
              {renderSource(entityType, entityId)}
            </div>
            <div className="col-sm-6">
              <div className="text-muted small" title={MODEL_NOTE}>Model</div>
              <SwimCoachModelBadges models={models} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderSummary = () => {
    const { conversation } = this.state
    if (!conversation) return null
    const summary = conversation.get('summary')
    if (!summary) return null
    const summarizedThroughDate = conversation.get('summarizedThroughDate')
    const summarizedMessageCount = conversation.get('summarizedMessageCount')
    const hasMeta = typeof summarizedMessageCount === 'number' ||
      Boolean(summarizedThroughDate)
    return (
      <div className="card mb-3">
        <div className="card-header">
          <b>Conversation Summary</b>
          <span className="text-muted small ms-2">
            (compressed history context)
          </span>
        </div>
        <div className="card-body">
          <div style={{ whiteSpace: 'pre-wrap' }}>{summary}</div>
          {hasMeta && (
            <div className="row mt-3">
              {typeof summarizedMessageCount === 'number' && (
                <div className="col-sm-4">
                  <div className="text-muted small">Summarized Messages</div>
                  {summarizedMessageCount}
                </div>
              )}
              {summarizedThroughDate && (
                <div className="col-sm-4">
                  <div className="text-muted small">Summarized Through</div>
                  {summarizedThroughDate.toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  renderToolCalls = (message) => {
    const toolCalls = message.get('toolCalls')
    if (!toolCalls || !toolCalls.length) return null
    return (
      <div className="mt-2">
        {toolCalls.map((toolCall, index) => (
          <details
            key={index}
            className="small bg-light border rounded mb-1"
          >
            <summary className="px-2 py-1">
              <span className="badge text-bg-secondary me-2">tool</span>
              <code>{toolCall.toolName}</code>
            </summary>
            <div className="px-2 pb-2">
              <div className="text-muted small">Arguments</div>
              <pre className="small mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                {formatJson(toolCall.args)}
              </pre>
              <div className="text-muted small">Result</div>
              <pre className="small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {formatJson(toolCall.result)}
              </pre>
            </div>
          </details>
        ))}
      </div>
    )
  }

  renderMessage = (message, index) => {
    const author = message.get('author')
    const isUser = author === 'user'
    const alignClass = isUser ? 'justify-content-end' : 'justify-content-start'
    const bgClass = isUser ? 'bg-primary text-white' : 'bg-light'
    const statusMeta = MESSAGE_STATUS_META[message.get('status')]
    const model = message.get(MODEL_FIELD)
    // Outline the whole card, not just the badge: the tell for these is a reply
    // that stops mid-thought, which is easy to read straight past when skimming.
    // Colour it off the same status as the badge — a red outline on a row that
    // is merely still streaming would overstate it.
    const borderClass = statusMeta ? ' ' + statusMeta.borderClass : ''
    return (
      <div key={index} className={'row mb-2 ' + alignClass}>
        <div className="col-md-8">
          <div className={'card ' + bgClass + borderClass}>
            <div className="card-body py-2 px-3">
              <div className="d-flex justify-content-between small mb-1">
                <b>
                  {isUser ? 'User' : 'Assistant'}
                  {model && (
                    <span className="ms-2">
                      <SwimCoachModelBadges models={[model]} />
                    </span>
                  )}
                  {statusMeta && (
                    <span
                      className={'badge ms-2 ' + statusMeta.badgeClass}
                      title={statusMeta.title}
                    >
                      {message.get('status')}
                    </span>
                  )}
                </b>
                <span className={isUser ? 'text-white-50' : 'text-muted'}>
                  {message.get('createdAt')
                    ? message.get('createdAt').toLocaleString()
                    : ''}
                </span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {message.get('content')}
              </div>
              {this.renderToolCalls(message)}
              {!isUser && this.renderMessageRating(message)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderMessages = () => {
    const { messages } = this.state
    if (!messages) return (<i>Loading...</i>)
    if (!messages.length) {
      return (<span className="text-muted">No messages</span>)
    }
    return messages.map((message, index) => this.renderMessage(message, index))
  }

  renderErrorBanners = () => {
    const { loadError, safetyReviewsError, evaluationsError } = this.state
    return (
      <>
        {loadError && (
          <div className="alert alert-danger" role="alert">
            <b>Error loading conversation:</b> {loadError}
          </div>
        )}
        {safetyReviewsError && (
          <div className="alert alert-warning" role="alert">
            <b>Safety reviews unavailable:</b> {safetyReviewsError}.
            {' '}Lock state and messages are still shown below.
          </div>
        )}
        {evaluationsError && (
          <div className="alert alert-warning" role="alert">
            <b>Evaluator ratings unavailable:</b> {evaluationsError}.
            {' '}The transcript is still shown below, without ratings.
          </div>
        )}
      </>
    )
  }

  render = () => {
    return (
      <div className="container">
        <br/>
        <h1>Swim Coach Conversation</h1>
        <p className="text-muted">
          User IDs only — names and emails are hidden for privacy.
        </p>
        {this.renderErrorBanners()}
        {this.renderLockActionError()}
        {this.renderCopyButton()}
        {this.renderLockControls()}
        {this.renderLockBanner()}
        {this.renderHeader()}
        {this.renderRatingSummary()}
        {this.renderSummary()}
        {this.renderSafetyReviews()}
        {this.renderMessages()}
        <br/>
      </div>
    )
  }
}
